import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success("Connexion réussie");
      const from = location.state?.from || (u.role === "admin" ? "/admin" : u.role === "artist" ? "/pilote/dashboard" : "/");
      nav(from);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden border-r border-white/10">
        <img src="https://images.pexels.com/photos/3656773/pexels-photo-3656773.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
        <div className="relative p-12 h-full flex flex-col justify-end">
          <div className="font-display text-6xl uppercase leading-none">Rentrez<br />dans le<br />mouvement.</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00]">Connexion</div>
          <h1 className="font-display text-6xl uppercase mt-2">Bon retour.</h1>
          <div className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Email</label>
              <input data-testid="login-email" type="email" required autoCapitalize="off" autoCorrect="off" spellCheck="false" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Mot de passe</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
            </div>
            <button data-testid="login-submit" disabled={loading}
              className="w-full py-4 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-50">
              {loading ? "..." : "Se connecter"}
            </button>
            <div className="text-sm text-white/60 text-center">
              <Link to="/mot-de-passe-oublie" data-testid="login-forgot-link" className="text-white/70 hover:text-[#FF3B00] uppercase tracking-widest text-xs">
                Mot de passe oublié ?
              </Link>
            </div>
            <div className="text-sm text-white/60 text-center">
              Pas encore de compte ? <Link to="/register" className="text-[#FF3B00]">Créer un compte</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "fan", artist_name: "", bio: "" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, email: form.email.trim().toLowerCase() };
      if (form.role === "fan") { delete payload.artist_name; delete payload.bio; }
      const u = await register(payload);
      toast.success(u.role === "artist" ? "Compte pilote créé — validation en cours" : "Compte créé");
      nav(u.role === "artist" ? "/pilote/dashboard" : "/");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00]">Inscription</div>
          <h1 className="font-display text-6xl uppercase mt-2">Rejoindre.</h1>

          <div className="mt-6 grid grid-cols-2 gap-2">
            {["fan", "artist"].map((r) => (
              <button type="button" key={r} onClick={() => setForm({ ...form, role: r })}
                data-testid={`role-${r}`}
                className={`py-3 uppercase tracking-widest text-xs border ${form.role === r ? "bg-[#FF3B00] border-[#FF3B00] text-white" : "border-white/20 text-white/70 hover:border-white"}`}>
                {r === "fan" ? "Je suis fan" : "Je suis pilote"}
              </button>
            ))}
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Nom {form.role === "artist" && "(légal)"}</label>
              <input data-testid="reg-name" required value={form.name} onChange={update("name")}
                className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
            </div>
            {form.role === "artist" && (
              <>
                <div className="border border-[#FF3B00] bg-[#FF3B00]/5 p-4 text-sm" data-testid="artist-terms-memo">
                  <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00] mb-2">À lire avant de vous inscrire</div>
                  <p className="text-white/80">
                    En créant votre studio, une <span className="font-bold text-[#FF3B00]">commission de 15%</span> est appliquée par défaut sur chaque vente. Ce taux couvre les frais de la plateforme, le paiement sécurisé et l'assistance.
                  </p>
                  <p className="mt-3 text-white/80">
                    Vous souhaitez négocier un taux personnalisé pour votre projet ? Contactez-nous :
                  </p>
                  <ul className="mt-2 space-y-1 text-white/90">
                    <li>📧 <a href="mailto:merchhub8@gmail.com" className="text-[#FF3B00] underline hover:no-underline">merchhub8@gmail.com</a></li>
                    <li>📱 Instagram — DM <span className="text-[#FF3B00]">@merchhub</span></li>
                  </ul>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Surnom du pilote</label>
                  <input data-testid="reg-artist-name" required value={form.artist_name} onChange={update("artist_name")}
                    className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Bio</label>
                  <textarea rows={3} data-testid="reg-bio" value={form.bio} onChange={update("bio")}
                    className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
                </div>
              </>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Email</label>
              <input data-testid="reg-email" type="email" required autoCapitalize="off" autoCorrect="off" spellCheck="false" value={form.email} onChange={update("email")}
                className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Mot de passe (min. 6 car.)</label>
              <input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={update("password")}
                className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
            </div>
            <button data-testid="reg-submit" disabled={loading}
              className="w-full py-4 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-50">
              {loading ? "..." : "Créer mon compte"}
            </button>
            <div className="text-sm text-white/60 text-center">
              Déjà membre ? <Link to="/login" className="text-[#FF3B00]">Se connecter</Link>
            </div>
          </div>
        </form>
      </div>
      <div className="hidden lg:block relative overflow-hidden border-l border-white/10 order-1 lg:order-2">
        <img src="https://images.pexels.com/photos/7971872/pexels-photo-7971872.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
        <div className="relative p-12 h-full flex flex-col justify-end">
          <div className="font-display text-6xl uppercase leading-none">Votre merch,<br />vos règles.</div>
        </div>
      </div>
    </div>
  );
}
